import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import type { MortgageSearchParams } from "../Calculator/types";
import * as Step from "./Steps";
import type {
	MortgageLeadCalcRequest,
	MortgageLeadCalcResponse,
	MortgageLeadFormVariant,
	ProductRequisitionTime,
} from "./types";
import "./style.scss";

export interface MortgageLeadFormFields extends AbstractLeadFormFields {
	loanAmount?: number;
	productRequisitionTime: ProductRequisitionTime;
}

export interface MortgageLeadFormProps<SearchParams>
	extends AbstractLeadFormProps<MortgageLeadCalcRequest, MortgageLeadCalcResponse, SearchParams> {
	variant?: MortgageLeadFormVariant;
	withLoanAmount?: boolean;
}

/**
 * @Shortcode
 *
 * @title "Lakáshitel cikkvégi leadform"
 * @shortcode "mortgage_lead"
 * @slug "lakashitel"
 * @type "leadform"
 */
export class MortgageLeadForm<
	FormFields extends MortgageLeadFormFields = MortgageLeadFormFields,
	SearchParams = MortgageSearchParams,
	Props extends MortgageLeadFormProps<SearchParams> = MortgageLeadFormProps<SearchParams>,
	State extends AbstractLeadFormState<FormFields> = AbstractLeadFormState<FormFields>,
> extends AbstractLeadForm<MortgageLeadCalcRequest, MortgageLeadCalcResponse, FormFields, Props, State> {
	// EventParams
	protected readonly productTypes: string = "MORTGAGE";
	protected readonly componentType: EventComponentType = "leadform";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget lakáshitel témában!",
		colorScheme: "light",
	};

	protected renderSteps(): ReactNode {
		const request = this.getCalculatorRequest();
		const response = this.getCalculatorResponse();
		const { title, variant, crossSellOption, withLoanAmount } = this.props;
		const {
			leadId,
			values: { email },
			submitState,
		} = this.state;

		return (
			<>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-lead"
					title={title}
					withLoanAmount={(!request?.loanAmount && variant !== "consumerFriendly") || withLoanAmount}
					variant={variant}
					submitState={submitState}
					crossSellOption={!!request?.loanAmount || variant === "consumerFriendly" || crossSellOption}
				/>
				<Step.SuccessPage
					stepName="success-lead"
					leadId={leadId}
					userEmail={email}
					ga4SendEventParameters={{
						providerName: response?.bankName,
						productName: response?.productName,
						productId: response?.id,
					}}
				/>
			</>
		);
	}

	protected onStep(target: string, sendEventEnabled = true): void {
		if (target === "success-lead") {
			this.sendLead()
				.then(() => {
					super.onStep(target, false);
				})
				.catch((error: Error) => {
					// TODO: Hibakezelés
					logError(error);
				});
		} else {
			super.onStep(target, sendEventEnabled);
		}
	}

	protected getLeadData(): LeadRequest<MortgageLeadCalcRequest, MortgageLeadCalcResponse> {
		const data = {
			...super.getLeadData(),
			calculatorType: "MORTGAGE" as const,
			productType: "MORTGAGE" as const,
			cityName: undefined,
		};
		data.administrationLocation = data.cityId;
		data.cityId = undefined;

		return data;
	}

	protected getCalculatorRequest(): MortgageLeadCalcRequest {
		const {
			values: { loanAmount, productRequisitionTime },
		} = this.state;

		const request = {
			...super.getCalculatorRequest(),
			productRequisitionTime,
		};

		if (loanAmount) {
			request.loanAmount = loanAmount;
		}

		if (!request.realEstateValue) {
			request.realEstateValue = request.loanAmount * 2;
		}

		return request;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-mortgages";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const request = this.getCalculatorRequest();
		const response = this.getCalculatorResponse();

		const {
			values: { lastName, firstName },
		} = this.state;

		return {
			lastName,
			firstName,
			productName: response?.productName,
			loanAmount: request?.loanAmount,
			loanAmountText: formatText(request?.loanAmount, "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "MORTGAGE";
	}

	protected getClassName(): string {
		return `${super.getClassName()} mortgage-lead-form`;
	}

	protected getComponentName(): string {
		return "MortgageLeadForm";
	}
}

export default MortgageLeadForm;
